import { useState, useCallback } from 'react';

const LANG_STORAGE_KEY = 'eraf.lang';

function getInitialLang(): 'ko' | 'en' {
  const stored = localStorage.getItem(LANG_STORAGE_KEY);
  if (stored === 'en') return 'en';
  return 'ko';
}

interface LanguageToggleProps {
  /** 버튼 크기 — 'sm' = 28px, 'md' = 36px (기본) */
  size?: 'sm' | 'md';
}

export function LanguageToggle({ size = 'md' }: LanguageToggleProps) {
  const [lang, setLang] = useState<'ko' | 'en'>(getInitialLang);

  const toggle = useCallback(() => {
    const next: 'ko' | 'en' = lang === 'ko' ? 'en' : 'ko';
    setLang(next);
    localStorage.setItem(LANG_STORAGE_KEY, next);
    document.documentElement.setAttribute('lang', next);
    // i18next 변경 — window.__eraf_i18n 인터페이스 (i18n.ts 에서 등록)
    if (typeof window !== 'undefined') {
      const w = window as Window & {
        __eraf_i18n?: { changeLanguage: (l: string) => void };
      };
      w.__eraf_i18n?.changeLanguage(next);
    }
  }, [lang]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle();
      }
    },
    [toggle]
  );

  const btnSize = size === 'sm' ? '28px' : '36px';
  const label =
    lang === 'ko'
      ? '언어 전환 — Switch to English'
      : 'Switch language — 한국어로 변경';

  return (
    <button
      type="button"
      onClick={toggle}
      onKeyDown={handleKeyDown}
      aria-label={label}
      title={label}
      aria-pressed={lang === 'en'}
      data-testid="language-toggle"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        height: btnSize,
        padding: '0 var(--spacing-2)',
        borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--color-surface-border)',
        background: 'var(--color-surface-muted)',
        color: 'var(--color-text-secondary)',
        cursor: 'pointer',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 600,
        fontFamily: 'var(--font-family-sans)',
        transition: 'background 0.15s ease, border-color 0.15s ease',
        flexShrink: 0,
      }}
    >
      <span aria-hidden="true" style={{ fontSize: '11px', letterSpacing: 0 }}>
        {/* globe unicode */}
        {'🌐'}
      </span>
      {lang === 'ko' ? 'KO' : 'EN'}
    </button>
  );
}
